import {
    APPOINTMENT_DATE_BASIS_HINTS,
    APPOINTMENT_DATE_BASIS_LABELS,
    AppointmentBreakdownType,
    AppointmentDateBasis,
    AppointmentFinancialBreakdown,
} from '@/lib/api/financial/financial-dashboard-api'
import { useServiceLabel } from '@/lib/hooks/use-service-label'
import {
    MdAssignment,
    MdAttachMoney,
    MdFilterList,
    MdFunctions,
    MdLocalHospital,
    MdMedicalServices,
    MdPercent,
    MdShowChart,
    MdWarning,
} from 'react-icons/md'

interface AppointmentBreakdownCardsProps {
    items: AppointmentFinancialBreakdown[]
    dateBasis: AppointmentDateBasis
    onDateBasisChange: (basis: AppointmentDateBasis) => void
    /** Recorte de cirurgia no card de pedido. Só existe na saúde. */
    surgicalOnly: boolean
    onSurgicalOnlyChange: (value: boolean) => void
}

const formatCurrency = (cents: number) =>
    new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    }).format(cents / 100)

const ICONS: Record<AppointmentBreakdownType, typeof MdLocalHospital> = {
    CONSULTATION: MdLocalHospital,
    EXAM: MdAssignment,
    ORDER: MdMedicalServices,
}

const ACCENTS: Record<AppointmentBreakdownType, string> = {
    CONSULTATION: 'text-info',
    EXAM: 'text-warning',
    ORDER: 'text-accent',
}

const BASIS_ORDER: AppointmentDateBasis[] = ['sold', 'created', 'scheduled', 'paid']

/**
 * Total, lucro e ticket médio por tipo de atendimento.
 *
 * O vocabulário vem do segmento (`useServiceLabel`): na saúde são Consultas,
 * Exames e Procedimentos; fora dela o backend só apura o pedido, e é "Pedidos"
 * que aparece. O bloco não decide o que exibir — mostra o que veio.
 */
export default function AppointmentBreakdownCards({
    items,
    dateBasis,
    onDateBasisChange,
    surgicalOnly,
    onSurgicalOnlyChange,
}: AppointmentBreakdownCardsProps) {
    const labels = useServiceLabel()

    if (!items.length) return null

    // Soma dos tipos exibidos — fora da saúde é o próprio total de pedidos, e é
    // por isso que sai do que veio, não de uma constante de três tipos.
    //
    // A contagem sozinha não responde a pergunta que se faz olhando o bloco ("quanto
    // isso deu?"): os três cards trazem o valor cada um, e somá-los de cabeça é o que
    // o operador estava fazendo. O selo passa a trazer também a receita e o lucro do
    // conjunto — o custo fica implícito na diferença e já aparece card a card.
    const totals = items.reduce(
        (sum, item) => ({
            count: sum.count + item.count,
            total: sum.total + item.total,
            cost: sum.cost + item.cost,
            profit: sum.profit + item.profit,
            costUnknownCount: sum.costUnknownCount + item.costUnknownCount,
        }),
        { count: 0, total: 0, cost: 0, profit: 0, costUnknownCount: 0 },
    )

    const titles: Record<AppointmentBreakdownType, string> = {
        CONSULTATION: labels.Consultations,
        EXAM: labels.Exams,
        ORDER: labels.Orders,
    }

    return (
        <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <h2 className="text-base font-semibold text-base-content">
                        Por tipo de atendimento
                    </h2>
                    <p className="text-xs text-base-content/60">
                        Apenas o que a empresa tem a receber.{' '}
                        {APPOINTMENT_DATE_BASIS_HINTS[dateBasis]}.
                    </p>
                    <div className="mt-2 inline-flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-secondary/30 bg-secondary/10 px-3 py-1.5">
                        <span className="flex items-center gap-2">
                            <MdFunctions className="text-base text-secondary" />
                            <span className="text-sm font-bold text-secondary">
                                {totals.count.toLocaleString('pt-BR')}
                            </span>
                            <span className="text-xs text-secondary/80">
                                no total do período
                            </span>
                        </span>
                        <span className="h-3 w-px bg-secondary/30" aria-hidden />
                        <span className="flex items-center gap-1.5">
                            <span className="text-xs text-secondary/80">Total</span>
                            <span
                                className="text-sm font-bold text-secondary"
                                title={formatCurrency(totals.total)}
                            >
                                {formatCurrency(totals.total)}
                            </span>
                        </span>
                        <span className="h-3 w-px bg-secondary/30" aria-hidden />
                        <span className="flex items-center gap-1.5">
                            <span className="text-xs text-secondary/80">Custo</span>
                            <span
                                className="text-sm font-bold text-warning"
                                title={formatCurrency(totals.cost)}
                            >
                                {formatCurrency(totals.cost)}
                            </span>
                        </span>
                        <span className="h-3 w-px bg-secondary/30" aria-hidden />
                        <span className="flex items-center gap-1.5">
                            <span className="text-xs text-secondary/80">Lucro</span>
                            <span
                                className={`text-sm font-bold ${
                                    totals.profit < 0 ? 'text-error' : 'text-primary'
                                }`}
                                title={formatCurrency(totals.profit)}
                            >
                                {formatCurrency(totals.profit)}
                            </span>
                        </span>
                        {/* Linha sem repasse apurado entra no total com custo zero:
                            o lucro somado fica maior do que é, e o aviso é o que
                            impede a leitura errada. */}
                        {totals.costUnknownCount > 0 && (
                            <span className="flex items-center gap-1 text-xs text-warning">
                                <MdWarning />
                                {totals.costUnknownCount} sem custo apurado
                            </span>
                        )}
                    </div>
                </div>

                <div className="form-control">
                    <label className="label py-0" htmlFor="dashboard-date-basis">
                        <span className="label-text text-xs font-medium">
                            Contar período por
                        </span>
                    </label>
                    <select
                        id="dashboard-date-basis"
                        className="select select-bordered select-sm w-52"
                        value={dateBasis}
                        onChange={e =>
                            onDateBasisChange(e.target.value as AppointmentDateBasis)
                        }
                    >
                        {BASIS_ORDER.map(basis => (
                            <option key={basis} value={basis}>
                                {APPOINTMENT_DATE_BASIS_LABELS[basis]}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {items.map(item => {
                    const Icon = ICONS[item.type]
                    const accent = ACCENTS[item.type]
                    const margin =
                        item.total > 0
                            ? Math.round((item.profit / item.total) * 1000) / 10
                            : null

                    return (
                        <div
                            key={item.type}
                            className="bg-base-100 rounded-xl border border-base-300/50 p-4 flex flex-col gap-3 shadow-sm"
                        >
                            <div className="flex items-center justify-between gap-2">
                                <div className="flex items-center gap-2 min-w-0">
                                    <span
                                        className={`w-9 h-9 rounded-lg bg-base-200 flex items-center justify-center text-xl shrink-0 ${accent}`}
                                    >
                                        <Icon />
                                    </span>
                                    <span className="font-semibold text-base-content truncate">
                                        {titles[item.type]}
                                    </span>
                                </div>
                                <div className="flex items-center gap-1 shrink-0">
                                    <span className="text-xs text-base-content/50">
                                        {item.count.toLocaleString('pt-BR')} no período
                                    </span>
                                    {/* Cirurgia é atributo do item de catálogo, e só
                                        o pedido tem. Fora da saúde a distinção não
                                        existe — o menu nem aparece. */}
                                    {item.type === 'ORDER' && labels.isHealth && (
                                        <div className="dropdown dropdown-end">
                                            <button
                                                tabIndex={0}
                                                type="button"
                                                aria-label="Filtrar este card"
                                                className={`btn btn-ghost btn-xs btn-circle ${
                                                    surgicalOnly ? 'text-secondary' : ''
                                                }`}
                                            >
                                                <MdFilterList className="text-base" />
                                            </button>
                                            <ul
                                                tabIndex={0}
                                                className="dropdown-content menu menu-sm z-10 mt-1 w-56 rounded-box border border-base-300/60 bg-base-100 p-2 shadow-lg"
                                            >
                                                <li>
                                                    <label className="flex cursor-pointer items-center gap-2">
                                                        <input
                                                            type="checkbox"
                                                            className="checkbox checkbox-sm checkbox-secondary"
                                                            checked={surgicalOnly}
                                                            onChange={e =>
                                                                onSurgicalOnlyChange(
                                                                    e.target.checked,
                                                                )
                                                            }
                                                        />
                                                        <span className="text-sm">
                                                            Apenas cirúrgicos
                                                        </span>
                                                    </label>
                                                </li>
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Filtro escondido em menu muda o número sem explicar
                                por quê. O selo é o que impede a leitura errada. */}
                            {item.type === 'ORDER' && surgicalOnly && (
                                <button
                                    type="button"
                                    onClick={() => onSurgicalOnlyChange(false)}
                                    className="badge badge-secondary badge-sm gap-1 w-fit"
                                    title="Remover o filtro"
                                >
                                    Apenas cirúrgicos
                                    <span aria-hidden>×</span>
                                </button>
                            )}

                            {/* Duas em cima (Total à esquerda, Lucro à direita) e o
                                ticket médio embaixo, ocupando a largura toda.
                                Em três colunas, "R$ 25.003,45" não cabia e era
                                cortado no meio — um valor truncado é pior que
                                uma linha a mais. */}
                            <div className="flex flex-col gap-2">
                                <div className="grid grid-cols-2 gap-2">
                                    <Metric
                                        label="Total"
                                        value={formatCurrency(item.total)}
                                        icon={<MdAttachMoney />}
                                        tone="text-success"
                                    />
                                    <Metric
                                        label="Lucro"
                                        value={formatCurrency(item.profit)}
                                        icon={<MdShowChart />}
                                        tone={
                                            item.profit < 0
                                                ? 'text-error'
                                                : 'text-primary'
                                        }
                                        align="right"
                                    />
                                </div>
                                <Metric
                                    label="Ticket médio"
                                    value={formatCurrency(item.averageTicket)}
                                    icon={<MdPercent />}
                                    tone="text-info"
                                    inline
                                />
                            </div>

                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-base-content/60">
                                {margin != null && (
                                    <span>
                                        Margem{' '}
                                        {margin.toString().replace('.', ',')}%
                                    </span>
                                )}
                                <span>Custo {formatCurrency(item.cost)}</span>
                                {/* Linha sem repasse snapshotado fica fora do lucro.
                                    Sem este aviso, o número parece cobrir tudo. */}
                                {item.costUnknownCount > 0 && (
                                    <span className="flex items-center gap-1 text-warning">
                                        <MdWarning />
                                        {item.costUnknownCount} sem custo apurado
                                    </span>
                                )}
                                {item.truncated && (
                                    <span className="flex items-center gap-1 text-warning">
                                        <MdWarning />
                                        Recorte parcial — reduza o período
                                    </span>
                                )}
                            </div>
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

function Metric({
    label,
    value,
    icon,
    tone,
    align = 'left',
    inline = false,
}: {
    label: string
    value: string
    icon: React.ReactNode
    tone: string
    align?: 'left' | 'right'
    /** Rótulo e valor na MESMA linha — cabe mais dígito. */
    inline?: boolean
}) {
    const labelEl = (
        <span className="text-[10px] uppercase tracking-wide text-base-content/50 font-medium flex items-center gap-1 shrink-0">
            <span className={tone}>{icon}</span>
            <span>{label}</span>
        </span>
    )

    // `title` sempre: mesmo com o layout novo, um valor na casa dos milhões
    // ainda pode faltar espaço, e o número inteiro fica a um hover.
    const valueEl = (
        <span className={`font-bold ${tone} ${inline ? 'text-sm' : 'text-base'}`} title={value}>
            {value}
        </span>
    )

    if (inline) {
        return (
            <div className="flex items-center justify-between gap-2 border-t border-base-300/40 pt-2 min-w-0">
                {labelEl}
                {valueEl}
            </div>
        )
    }

    return (
        <div
            className={`flex flex-col gap-0.5 min-w-0 ${
                align === 'right' ? 'items-end text-right' : ''
            }`}
        >
            {labelEl}
            {valueEl}
        </div>
    )
}
